<script lang="ts">
	import { beforeUpdate } from 'svelte';

	// Stores
		import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { format } from 'svelte-i18n';

	// Services and configs
	import { devices_config } from 'src/configs/devices';

	// Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_control_component_channel_structure,
		is_gen_2,
		real_id,
		defaultTempFavPositions,
	} from 'src/helpers/interface_helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';

	// Design System Components
	import { Alert } from '@Shelly/shelly-design-system';

	// Components
	import TimerRemaining from 'src/components/TimerRemaining/TimerRemaining.svelte';
	import DeviceViewStateWrapper from 'src/components/Sidepanel/DeviceViews/DeviceViewStateWrapper.svelte';
	import BluTrvMainControlV2 from 'src/components/DevicesSettings/VirtualComponents/BluTRV/BluTrvMainControlV2.svelte';
	import DeviceNotificationLabel from 'src/components/Sidepanel/DeviceNotificationLabel.svelte';
	import DeviceLabelList from 'src/components/Sidepanel/DeviceViews/DeviceLabelList.svelte';

	// Types
	import type { Device } from 'src/types/Device';

	/**
	 * Main control tab for the SBTR-001AEU (BluTRV / Gen3 BLE TRV) sidepanel drawer.
	 * Layout aligned with SHTRV-01 (Gen1 TRV): DeviceViewStateWrapper, staticTop (labels, timer, alert), staticBottom (TRV control).
	 * Uses status_configurator / getBluTRVStatus for labels and BluTrvMainControl for Gen3/BLE TRV controls.
	 *
	 * @prop device Active device selected in the sidepanel drawer. Expected to be SBTR-001AEU (BluTRV).
	 */
	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceConfig = devices_config?.[device?.type];

	$: _statusesRaw =
		status?.online && deviceConfig ? status_configurator(status, device, settings) : [];
	$: _statuses = Array.isArray(_statusesRaw) ? _statusesRaw : [];
	$: labelList = _statuses.filter((e) => typeof e.main === 'undefined' && e.name !== 'state');
	$: combinedLabelList = [...Object.values(timerLabels), ...labelList];

	$: tempUnit = device?.ht_ui?.temperature_unit || 'C';
	$: _rawTempFav = device?.temp_fav_positions ?? defaultTempFavPositions(tempUnit);
	$: temp_fav_positions = Array.isArray(_rawTempFav) ? _rawTempFav : [];

	let timerLabels: Record<string, any> = {};
	let timer = false;

	beforeUpdate(() => {
		timer =
			is_gen_2(device) &&
			typeof status?.[get_gen_2_component_channel_structure(device)]?.timer_started_at !==
				'undefined';
	});
</script>

<DeviceViewStateWrapper {device}>
	<div
		slot="staticTop"
		class="label-list-wrapper mb-s d-flex flex-direction-column justify-content-center"
	>
		{#if timer}
			{#key status?.[get_gen_2_control_component_channel_structure(device)]?.timer_started_at}
				<TimerRemaining timerKey="gen2_main" {device} {status} {settings} bind:timerLabels />
			{/key}
		{/if}
		<DeviceLabelList labelList={combinedLabelList} />

		<DeviceNotificationLabel deviceId={device?.id} />
		{#if !device?.shared && temp_fav_positions.filter((pos) => pos?.active).length === 0}
			<Alert
				dismissable={true}
				type="info"
				isHeadingBold
				heading={$format('settings_title_favorites', {
					default: 'Favorite positions',
				})}
				message={$format('alert_info_heading_temp_fav_positions', {
					default:
						'You can enable or disable temperature favorite positions from sensor settings',
				})}
				cta={{
					title: $format('button_enable_disable', {
						default: 'Enable/Disable',
					}),
				}}
				on:cta_click={() => {
					navigateToSetting(
						device.id,
						'sensor_settings',
						'temperature_favorite_positions',
						false,
					);
				}}
			/>
		{/if}
	</div>

	<div class="flex-full-height" slot="content">
		<BluTrvMainControlV2 {device} statuses={_statuses} hideTempFavPositionsAlert={true} />
	</div>
</DeviceViewStateWrapper>
